import * as React from "react";
import { useState } from "react";
import { observer } from "mobx-react";
import { useStores } from "../../../../stores";
import { API } from "../../../../core";
import { MethodRequest } from "../../../../constants";
import { Button, InputField } from "../../../common";
import {
	Actions,
	Container,
	FormWrapper,
	SuccessBox,
	SuccessEmail,
	SuccessText,
} from "./EmailChangeForm.styled";

interface EmailChangeResponse {
	success: true;
	status: "confirmation_required";
	message: string;
	data: {
		new_email: string;
		expires_at: string;
		resend_available_at: string;
	};
}

export const EmailChangeForm = observer(() => {
	const { authStore, notificationsStore } = useStores();
	const { userStore } = authStore;

	const [newEmail, setNewEmail] = useState("");
	const [currentPassword, setCurrentPassword] = useState("");
	const [isLoading, setIsLoading] = useState(false);

	const [confirmationEmail, setConfirmationEmail] = useState("");
	const [resendAvailableAt, setResendAvailableAt] = useState<string | null>(
		null,
	);

	const handleSubmit = async (
		event: React.FormEvent<HTMLFormElement>,
	) => {
		event.preventDefault();

		if (!newEmail.trim() || !currentPassword) {
			return;
		}

		setIsLoading(true);

		try {
			const response = await API.request<EmailChangeResponse>(
				"user/email-change/request",
				{
					method: MethodRequest.Post,
					body: API.getFormData({
						new_email: newEmail.trim().toLowerCase(),
						current_password: currentPassword,
					}),
				},
			);

			setConfirmationEmail(response.data.new_email);
			setResendAvailableAt(
				response.data.resend_available_at,
			);

			setCurrentPassword("");

			notificationsStore.add({
				title: "Confirmation email sent",
				message:
					"We sent a confirmation link to your new email address.",
			});
		} catch (error: any) {
			notificationsStore.error({
				message:
					error?.message ||
					error?.errors?.join("<br/>") ||
					"Unable to change email address.",
			});
		} finally {
			setIsLoading(false);
		}
	};

	const handleResend = async () => {
		setIsLoading(true);

		try {
			const response = await API.request<{
				success: true;
				data: {
					resend_available_at: string;
				};
			}>("user/email-change/resend", {
				method: MethodRequest.Post,
			});

			setResendAvailableAt(
				response.data.resend_available_at,
			);

			notificationsStore.add({
				title: "Confirmation email sent",
				message:
					"A new confirmation link has been sent to your email address.",
			});
		} catch (error: any) {
			notificationsStore.error({
				message:
					error?.message ||
					error?.errors?.join("<br/>") ||
					"Unable to resend confirmation email.",
			});
		} finally {
			setIsLoading(false);
		}
	};

	if (confirmationEmail) {
		return (
			<Container>
				<SuccessBox>
					<SuccessText>
						We sent a confirmation link to your new email
						address:
					</SuccessText>

					<SuccessEmail>
						{confirmationEmail}
					</SuccessEmail>

					<SuccessText>
						Open the link in the email to complete the
						change.
					</SuccessText>

					{resendAvailableAt && (
						<Button
							type="button"
							onClick={handleResend}
							isLoading={isLoading}
						>
							Resend Email
						</Button>
					)}
				</SuccessBox>
			</Container>
		);
	}

	return (
		<Container>
			<FormWrapper onSubmit={handleSubmit}>
				<InputField
					small
					type="email"
					label="Current Email"
					value={userStore.email}
					disabled
				/>

				<InputField
					small
					type="email"
					label="New Email"
					placeholder="Enter your new email"
					value={newEmail}
					onChange={(event) =>
						setNewEmail(event.target.value)
					}
				/>

				<InputField
					small
					type="password"
					label="Current Password"
					placeholder="Enter your current password"
					value={currentPassword}
					onChange={(event) =>
						setCurrentPassword(event.target.value)
					}
				/>

				<Actions data-action>
					<Button
						type="submit"
						isLoading={isLoading}
						disabled={
							isLoading ||
							!newEmail.trim() ||
							!currentPassword
						}
					>
						Change Email
					</Button>
				</Actions>
			</FormWrapper>
		</Container>
	);
});
